import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
/**
 * Use this to easily bind your actions to react-redux dispatch, all while
 * preserving type interface!
 *
 * Instead of doing this:
 * ```
 * const dispatch = useDispatch();
 * const foo = React.useCallback(() => dispatch(MyAction.action), [dispatch]);
 * ```
 *
 * You can do this:
 * ```
 * const foo = useBindDispatch(Actions.bar);
 * ```
 */
export const useBindDispatch = (action) => {
    return useDispatchCallback(dispatch => {
        return (...args) => {
            dispatch(action.action(...args));
        };
    }, [action]);
};
/**
 * Use this to create custom callbacks that involve dispatch.
 *
 * ```
 * const foo = useMakeDispatch(dispatch => (fizz: string, buzz: string) => {
 *   // do something with fizz and buzz
 *   dispatch(Actions.bar);
 *   return {fizz, buzz};
 * })
 * const foo: (fizz: string, buzz: string) => {fizz: string; buzz: string}
 * ```
 */
export const useDispatchCallback = (makeCallback, dependencyList) => {
    const dispatch = useDispatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useCallback(makeCallback(dispatch), [dispatch, ...dependencyList]);
};
